import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { TextInput, Services, Modal, Text, BubbleSelectBlock } from '@simplybenefits/sb-ui-library';
import { createAdvisor } from '../actions/advisors.js';
import FormControl from "@mui/material/FormControl";

const statusMapString = {
    1: 'Active',
    2: 'Disabled',
    9: 'Cancelled'
};

const statusMapInt = {
    Active: 1,
    Disabled: 2,
    Cancelled: 9
};

class AdvisorForm extends PureComponent {
    state = {
        firstname: '',
        lastname: '',
        email: '',
        company: '',
        brokerage: '',
        language: '',
        status: statusMapString[1],
        showConfirmForm: false,
        useMFA: false
    };

    _validateForm = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname);
        const lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const emailError = await Services.validateInput('email', this.state.email);
        const companyError = await Services.validateInput('company', this.state.company);
        const statusError = await Services.validateInput('status', this.state.status);
        const brokerageError = await Services.validateInput('checkEmpty', this.state.brokerage);
        const languageError = await Services.validateInput('language', this.state.language);

        this.setState({
            firstnameError,
            lastnameError,
            emailError,
            companyError,
            statusError,
            brokerageError,
            languageError
        });

        if (
            firstnameError ||
            lastnameError ||
            emailError ||
            companyError ||
            statusError ||
            brokerageError ||
            languageError
        ) {
            return false;
        }

        return true;
    };

    _createAdvisor = async () => {
        const isFormValid = await this._validateForm();

        const {
            firstname,
            lastname,
            email,
            company,
            status,
            brokerage,
            language,
            useMFA
        } = this.state;

        if (!isFormValid) {
            return false;
        }

        const data = {
            firstname,
            lastname,
            email,
            company,
            useMFA: Boolean(useMFA),
            status: statusMapInt[status],
            brokerageID: this.props.brokerages.find((brok) => brok.name === brokerage).brokerageID,
            language: language === "French" ? 'fr' : 'en'
        };

        this._clear();
        await this.props.dispatch(createAdvisor(data));
        return null;
    };

    _hideForm = () => {
        this.setState({
            showConfirmForm: false
        });
    };

    _clear = () => {
        this.setState({
            firstname: '',
            firstnameError: false,
            lastname: '',
            lastnameError: false,
            email: '',
            emailError: false,
            company: '',
            companyError: false,
            brokerage: '',
            brokerageError: false,
            language: '',
            languageError: false,
            useMFA: false
        });

        this.props.close();
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    render() {
        const brokerages = this.props.brokerages.map((brokerage) => ({
            key: brokerage.brokerageID,
            value: brokerage.name
        }));

        return (
            <Modal
                open={true}
                onClose={this.props.close}
                title="Add Advisor"
                primaryButton={{
                    label: "Create Advisor",
                    onClick: this._createAdvisor
                }}
                loader={this.props.loader}
            >
                <TextInput
                    label="First Name"
                    value={this.state.firstname}
                    onBlur={() => this._onBlur('firstname')}
                    onChange={(value) => this._handleInputChange('firstname', value)}
                    error={Boolean(this.state.firstnameError)}
                    helperText={this.state.firstnameError}
                    fullWidth
                    required
                    autoFocus
                />

                <TextInput
                    label="Last Name"
                    value={this.state.lastname}
                    onBlur={() => this._onBlur('lastname')}
                    onChange={(value) => this._handleInputChange('lastname', value)}
                    error={Boolean(this.state.lastnameError)}
                    helperText={this.state.lastnameError}
                    fullWidth
                    required
                />

                <TextInput
                    label="Email"
                    value={this.state.email}
                    onBlur={() => this._onBlur('email')}
                    onChange={(value) => this._handleInputChange('email', value)}
                    error={Boolean(this.state.emailError)}
                    helperText={this.state.emailError}
                    fullWidth
                    required
                />

                <TextInput
                    label="Company"
                    value={this.state.company}
                    onBlur={() => this._onBlur('company')}
                    onChange={(value) => this._handleInputChange('company', value)}
                    error={Boolean(this.state.companyError)}
                    helperText={this.state.companyError}
                    fullWidth
                    required
                />

                <TextInput
                    label="Brokerage"
                    value={this.state.brokerage}
                    onBlur={() => this._onBlur('brokerage')}
                    onChange={(value) => this._handleInputChange('brokerage', value)}
                    error={Boolean(this.state.brokerageError)}
                    helperText={this.state.brokerageError}
                    fullWidth
                    required
                    select
                    selectOptions={brokerages}
                />

                <TextInput
                    label="Language"
                    value={ this.state.language }
                    onBlur={() => this._onBlur('language')}
                    onChange={(value) => this._handleInputChange('language', value)}
                    error={Boolean(this.state.languageError)}
                    helperText={this.state.languageError}
                    select
                    selectOptions={[ 'English', 'French' ]}
                    fullWidth
                />

                <FormControl component="fieldset">
                    <Text
                        medium
                        light
                        margin="20px 0 0px 0"
                    >
                        Enforce Multi-factor Authentication (MFA)
                    </Text>

                    <Text
                        tiny
                        light
                        margin="5px 0 5px 0"
                    >
                        Enforce multi-factor authentication when attempting login.
                    </Text>

                    <BubbleSelectBlock
                        first={{
                            title: 'Yes',
                            selected: Boolean(this.state.useMFA) === true,
                            field: 'enforceMFAYes'
                        }}
                        second={{
                            title: 'No',
                            selected: Boolean(this.state.useMFA) === false,
                            field: 'enforceMFANo'
                        }}
                        onBlur={(e) => this._onBlur('useMFA', e)}
                        handleChange={ async (field) => {
                            if (field === 'enforceMFAYes') {
                                await this._handleInputChange('useMFA', true, true);
                            } else {
                                await this._handleInputChange('useMFA', false, true);
                            }
                        }}
                    />
                </FormControl>
            </Modal>
        );
    }
}

/**
 * @param state
 * @returns {{loading: *}}
 */
const mapStateToProps = (state) => ({
    brokerages: state.master.brokerages
});

export default connect(mapStateToProps)(AdvisorForm);